import { useEffect, useRef, useState } from "react";
import { Share2, Check, Copy, MessageCircle } from "lucide-react";

export default function ShareButton({ url, title, text, className = "", testId = "pdp-share-button" }) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const popRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("touchstart", onDoc);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("touchstart", onDoc);
        };
    }, [open]);

    const handleClick = async () => {
        const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
        if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
            try {
                await navigator.share({
                    title: title || "",
                    text: text || "",
                    url: shareUrl,
                });
                return;
            } catch (err) {
                // AbortError = user cancelled the native sheet — do not open fallback
                if (err && (err.name === "AbortError" || err.code === 20)) return;
                // Other errors fall through to fallback popover
            }
        }
        setOpen((v) => !v);
    };

    const copyLink = async () => {
        const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
        let ok = false;
        try {
            if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(shareUrl);
                ok = true;
            }
        } catch (_) {
            ok = false;
        }
        if (!ok && typeof document !== "undefined") {
            try {
                const ta = document.createElement("textarea");
                ta.value = shareUrl;
                ta.setAttribute("readonly", "");
                ta.style.position = "absolute";
                ta.style.left = "-9999px";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
                ok = true;
            } catch (_) {
                ok = false;
            }
        }
        if (ok) {
            setCopied(true);
            setTimeout(() => {
                setCopied(false);
                setOpen(false);
            }, 1200);
        }
    };

    const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
    const waText = `${text ? text + "\n" : ""}${shareUrl}`;
    const waHref = `https://wa.me/?text=${encodeURIComponent(waText)}`;

    return (
        <div className={`relative inline-block ${className}`} ref={popRef}>
            <button
                type="button"
                onClick={handleClick}
                aria-label="Share product"
                data-testid={testId}
                className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-display font-bold uppercase tracking-widest py-4 rounded-sm hover:border-[#00BCE5] hover:text-[#00BCE5] px-4 w-full"
            >
                <Share2 size={16} /> Share
            </button>

            {open && (
                <div
                    role="menu"
                    data-testid="pdp-share-popover"
                    className="absolute right-0 bottom-full mb-2 w-56 bg-[#121214] border border-[#27272A] rounded-sm shadow-xl z-40 overflow-hidden"
                >
                    <a
                        href={waHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-2 px-3 py-2.5 text-sm text-white hover:bg-[#18181B]"
                        data-testid="pdp-share-whatsapp"
                    >
                        <MessageCircle size={16} className="text-[#25D366]" />
                        Share on WhatsApp
                    </a>
                    <button
                        type="button"
                        onClick={copyLink}
                        className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-white hover:bg-[#18181B] border-t border-[#27272A]"
                        data-testid="pdp-share-copy-link"
                    >
                        {copied ? (
                            <>
                                <Check size={16} className="text-[#00BCE5]" />
                                <span>Copied</span>
                            </>
                        ) : (
                            <>
                                <Copy size={16} className="text-white/70" />
                                <span>Copy link</span>
                            </>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
}
